/* eslint-disable max-len */
import {
  QuestionCircleOutlined,
  SaveOutlined,
  SelectOutlined,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import DropdownMultiple from 'common/components/DropdownMultiple';
import { DropdownElement } from 'common/components/DropdownType';
import Editor from 'common/components/Editor';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import ManagementInfo from 'common/components/ManagementInfo';
import ModalConfirm from 'common/components/ModalConfirm';
import SelectFile from 'common/components/SelectFile';
import SeoPlugin from 'common/components/SeoPlugin';
import SeoSection, {
  SeoSectionActionProps,
} from 'common/components/SeoSection';
import { SeoFormTypes } from 'common/components/SeoSection/types';
import StatusLabel, { StatusButtons } from 'common/components/StatusLabel';
import getAllBannersService from 'common/services/banners';
import { setSessionStorage } from 'common/services/common/storage';
import {
  createBlogService,
  getBlogByIdService,
  updateBlogByIdService,
  updateBlogStatusService,
} from 'common/services/extends/blogs';
import { CreateUpdateBlogParamsTypes } from 'common/services/extends/blogs/types';
import LOCAL_STORAGE, { BUTTON_EXAMPLES, ROUTE_PATHS, socialList } from 'common/utils/constant';
import {
  delay,
  generateSlug,
  returnDefaultOG,
  returnOgData,
} from 'common/utils/functions';
import { updateBlogByIdSchema } from 'common/utils/schemas';
import roles from 'configs/roles';

export type BlogFormTypes = {
  displayOrder: number;
  publishedAt?: string;
  title: string;
  slug: string;
  description: string;
  content: string;
  thumbnail: string;
  thumbnailTitle?: string;
  thumbnailAlt?: string;
  status: number;
  relatedIds?: number[];
  categoryIds?: number[];
  tags?: string[];
  blogData?: {
    title: string;
    slug: string;
    bannerId?: number;
    description?: string;
    content: string;
    thumbnail?: Thumbnail;
    storyHook?: {
      title: string;
      description: string;
      thumbnail: Thumbnail;
    };
    items?: {
      title: string;
      description: string;
      thumbnail: Thumbnail;
    }[];
    summary?: {
      title: string;
      rows: {
        title: string;
        description: string;
        subTitle: string;
        thumbnail: Thumbnail;
      }[];
    };
    conclusion?: {
      title: string;
      description: string;
      thumbnail: Thumbnail;
    };
    footer?: {
      title: string;
      description: string;
      content: string;
    };
  };
};

const BlogEdit: React.FC<ActiveRoles> = ({
  roleCreate,
  roleUpdate,
  roleOther,
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const InputHidden: React.FC = () => null;

  /* Selectors */
  const { languageOptions, defaultWebsiteLanguage } = useAppSelector(
    (state) => state.system
  );

  /* States */
  const idParams = Number(searchParams.get('id'));
  const localeParams = searchParams.get('locale') || defaultWebsiteLanguage || '';
  const seoSectionRef = useRef<SeoSectionActionProps>(null);
  const [currentLang, setCurrentLang] = useState<string>(localeParams);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [confirm, setConfirm] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<number>(1);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [copyStatus, setCopyStatus] = useState<string>('');

  /* Variables */
  const defaultValues = {
    displayOrder: 1,
    publishedAt: dayjs().toISOString(),
    title: '',
    slug: '',
    description: '',
    content: '',
    thumbnail: '',
    blogData: {
      items: [
        {
          title: '',
          description: '',
          thumbnail: {
            path: '',
            title: '',
            alt: '',
          },
        },
      ],
      summary: {
        title: '',
        rows: [
          {
            title: '',
            description: '',
            subTitle: '',
            thumbnail: {
              path: '',
              title: '',
              alt: '',
            },
          },
        ],
      },
    },
  };

  const method = useForm<BlogFormTypes>({
    resolver: yupResolver(updateBlogByIdSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { isDirty } = method.formState;

  /* Queries */
  const { data: blogByIdData, isLoading } = useQuery(
    ['getBlogById', currentLang, idParams],
    () => {
      if (idParams) {
        return getBlogByIdService({
          id: idParams,
          locale: currentLang,
        });
      }
      return undefined;
    }
  );

  const {
    data: banners,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['banners-select'],
    queryFn: ({ pageParam = 1 }) => getAllBannersService({
      page: pageParam,
      limit: 30,
    }),
    getNextPageParam: (lastPage) => (lastPage.meta.page < lastPage.meta.totalPages
      ? lastPage.meta.page + 1
      : undefined),
    select: (data) => ({
      pages: data.pages.flatMap((page) => page.data),
      pageParams: data.pageParams,
    }),
  });

  const {
    fields: blockFields,
    append: appendBlock,
    remove: removeBlock,
  } = useFieldArray({
    control: method.control,
    name: 'blogData.items',
  });

  const {
    fields: agentFields,
    append: appendAgent,
    remove: removeAgent,
  } = useFieldArray({
    control: method.control,
    name: 'blogData.summary.rows',
  });

  const { mutate: updateBlogByIdMutate, isLoading: updateLoading } = useMutation(
    'updateBlogById',
    async (data: { id: number; params: CreateUpdateBlogParamsTypes }) => updateBlogByIdService(data.id, data.params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: () => {
        message.error(t('message.updateError'));
      },
    }
  );

  const { mutate: createBlogByIdMutate, isLoading: createLoading } = useMutation('createBlog', createBlogService, {
    onSuccess: () => {
      message.success(t('message.createSuccess'));
      navigate(`${ROUTE_PATHS.BLOG_MANAGEMENT}`);
    },
    onError: () => {
      message.error(t('message.createError'));
    },
  });

  const { mutateAsync: updateStatusMutate, isLoading: updateStatusLoading } = useMutation(
    'updateBlogStatus',
    async (data: { id: number; statusId: number }) => updateBlogStatusService(data.id, data.statusId),
    {
      onSuccess: (_, params) => {
        setStatus(params.statusId);
        message.success(t('message.updateStatusSuccess'));
        queryClient.invalidateQueries(['getBlogById', currentLang, idParams]);
      },
      onError: () => {
        message.error(t('message.updateStatusError'));
      },
    }
  );

  const onSubmit = async (addingFn?: (id: number) => void) => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }
    const formData = method.getValues();
    const published = dayjs(formData.publishedAt).toISOString();
    const dataSeoForm = await seoSectionRef.current?.handleForm();

    const blogParams: CreateUpdateBlogParamsTypes = {
      publishedAt: published,
      displayOrder: formData.displayOrder,
      relatedIds: formData.relatedIds,
      categoryIds: formData.categoryIds,
      status: formData.status,
      translations: {
        [currentLang]: {
          blogData: {
            title: formData.title,
            slug: formData.slug,
            bannerId: formData.blogData?.bannerId,
            content: formData.content,
            description: formData.description,
            thumbnail: {
              path: formData.thumbnail,
              title: formData.thumbnailTitle,
              alt: formData.thumbnailAlt,
            },
            storyHook: {
              title: formData.blogData?.storyHook?.title,
              description: formData.blogData?.storyHook?.description,
              thumbnail: {
                path: formData.blogData?.storyHook?.thumbnail?.path,
                title: formData.blogData?.storyHook?.thumbnail?.title,
                alt: formData.blogData?.storyHook?.thumbnail?.alt,
              },
            } as {
              title: string;
              description: string;
              thumbnail: Thumbnail;
            },
            items: formData.blogData?.items as {
              title: string;
              description: string;
              thumbnail: Thumbnail;
            }[],
            summary: formData.blogData?.summary as {
              title: string;
              rows: {
                title: string;
                description: string;
                subTitle: string;
                thumbnail: Thumbnail;
              }[];
            },
            conclusion: {
              title: formData.blogData?.conclusion?.title,
              description: formData.blogData?.conclusion?.description,
              thumbnail: {
                path: formData.blogData?.conclusion?.thumbnail?.path,
                title: formData.blogData?.conclusion?.thumbnail?.title,
                alt: formData.blogData?.conclusion?.thumbnail?.alt,
              },
            } as {
              title: string;
              description: string;
              thumbnail: Thumbnail;
            },
          },
          tags: formData.tags,
        },
      },
      seoData: {
        [currentLang]: {
          title: dataSeoForm?.seoTitle,
          description: dataSeoForm?.seoIntro,
          keywords: dataSeoForm?.seoKeyword,
          metaRobot: dataSeoForm?.metaRobot,
          structureData: dataSeoForm?.structuredData,
          metaViewport: dataSeoForm?.metaViewPort,
          canonicalUrl: dataSeoForm?.canonicalURL,
          image: dataSeoForm?.ogImage,
        },
      },
      ogData: returnOgData(currentLang, dataSeoForm?.mediaSocial),
    };

    if (idParams) {
      updateBlogByIdMutate(
        {
          id: idParams,
          params: blogParams,
        },
        {
          onSuccess: () => addingFn && addingFn(idParams),
        }
      );
    } else {
      createBlogByIdMutate(blogParams, {
        onSuccess: (data) => addingFn && addingFn(data.id),
      });
    }
  };

  const handlePreview = async () => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }
    const formData = method.getValues();
    const published = dayjs(formData.publishedAt).toISOString();
    const dataSeoForm = await seoSectionRef.current?.handleForm();
    const blogParams = {
      publishedAt: published,
      displayOrder: formData.displayOrder,
      relatedIds: formData.relatedIds,
      categoryIds: formData.categoryIds,
      status: formData.status,
      translations: {
        [currentLang]: {
          blogData: {
            title: formData.title,
            slug: formData.slug,
            content: formData.content,
            description: formData.description,
            thumbnail: {
              path: formData.thumbnail,
              title: formData.thumbnailTitle,
              alt: formData.thumbnailAlt,
            },
            bannerId: formData.blogData?.bannerId,
            storyHook: {
              title: formData.blogData?.storyHook?.title,
              description: formData.blogData?.storyHook?.description,
              thumbnail: {
                path: formData.blogData?.storyHook?.thumbnail?.path,
                title: formData.blogData?.storyHook?.thumbnail?.title,
                alt: formData.blogData?.storyHook?.thumbnail?.alt,
              },
            } as {
              title: string;
              description: string;
              thumbnail: Thumbnail;
            },
            items: formData.blogData?.items as {
              title: string;
              description: string;
              thumbnail: Thumbnail;
            }[],
            summary: formData.blogData?.summary as {
              title: string;
              rows: {
                title: string;
                description: string;
                subTitle: string;
                thumbnail: Thumbnail;
              }[];
            },
            conclusion: {
              title: formData.blogData?.conclusion?.title,
              description: formData.blogData?.conclusion?.description,
              thumbnail: {
                path: formData.blogData?.conclusion?.thumbnail?.path,
                title: formData.blogData?.conclusion?.thumbnail?.title,
                alt: formData.blogData?.conclusion?.thumbnail?.alt,
              },
            } as {
              title: string;
              description: string;
              thumbnail: Thumbnail;
            },
          },
          tags: formData.tags,
        },
      },
      seoData: {
        [currentLang]: {
          title: dataSeoForm?.seoTitle,
          description: dataSeoForm?.seoIntro,
          keywords: dataSeoForm?.seoKeyword,
          metaRobot: dataSeoForm?.metaRobot,
          structureData: dataSeoForm?.structuredData,
          metaViewport: dataSeoForm?.metaViewPort,
          canonicalUrl: dataSeoForm?.canonicalURL,
          image: dataSeoForm?.ogImage,
        },
      },
      ogData: returnOgData(currentLang, dataSeoForm?.mediaSocial),
    };
    const savingData = { type: 'blog', data: blogParams };
    setSessionStorage(LOCAL_STORAGE.PREVIEW_DATA, JSON.stringify(savingData));
    window.open('/preview', '_blank');
  };

  const changeLanguageAction = async (lang?: string) => {
    if (lang) {
      setConfirm(undefined);
      setLoadingTemplate(true);
      await delay(1000);
      setCurrentLang(lang as LanguageCodeTypes);
      setSearchParams(
        {
          id: String(idParams),
          locale: lang,
        },
        { replace: true }
      );
      setLoadingTemplate(false);
    }
  };

  const handleChangeLang = async (lang: LanguageCodeTypes) => {
    if (isDirty) {
      setConfirm(lang);
    } else changeLanguageAction(lang);
  };

  const handleChangeStatusAndSave = async (stat: number) => {
    if (!idParams) {
      await onSubmit(async (id) => {
        await updateStatusMutate({ id, statusId: stat });
      });
    } else {
      if (isDirty || seoSectionRef.current?.isFormDirty()) {
        await onSubmit(async (id) => {
          await updateStatusMutate({ id, statusId: stat });
        });
      }
      await updateStatusMutate({ id: idParams, statusId: stat });
    }
  };

  const submitForm = async () => {
    await onSubmit();
    queryClient.invalidateQueries(['getBlogById', currentLang, idParams]);
  };

  const [hidingSection, setHidingSection] = useState<
    Record<
      | 'blogData.storyHook'
      | 'blogData.items'
      | 'blogData.summary'
      | 'blogData.conclusion',
      boolean
    >
  >({
    'blogData.storyHook': true,
    'blogData.items': true,
    'blogData.summary': true,
    'blogData.conclusion': true,
  });

  const handleHidingSection = (
    fieldName:
      | 'blogData.storyHook'
      | 'blogData.items'
      | 'blogData.summary'
      | 'blogData.conclusion'
  ) => {
    setHidingSection((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName], // toggle true/false
    }));
    method.setValue(fieldName, undefined, { shouldDirty: true });
  };

  /* Effects */
  const defaultValueSEO: SeoFormTypes | undefined = useMemo(() => {
    if (!blogByIdData) {
      return undefined;
    }
    const seo = blogByIdData.seoData && blogByIdData.seoData[`${currentLang}`];
    const og = blogByIdData.ogData;
    return {
      seoTitle: seo?.title || '',
      seoIntro: seo?.description || '',
      seoKeyword: seo?.keywords || '',
      ogImage: seo?.image,
      metaRobot: seo?.metaRobot,
      metaViewPort: seo?.metaViewport,
      canonicalURL: seo?.canonicalUrl,
      structuredData: seo?.structureData,
      mediaSocial: returnDefaultOG(og, currentLang),
    } as SeoFormTypes;
  }, [currentLang, blogByIdData]);

  useEffect(() => {
    if (blogByIdData && blogByIdData.translations[currentLang]) {
      setStatus(blogByIdData.blogData.status);
      const {
        title,
        slug,
        thumbnail,
        content,
        description,
        items,
        storyHook,
        summary,
        conclusion,
        bannerId,
      } = blogByIdData.translations[currentLang];
      const objDefault = {
        title,
        slug,
        thumbnail: thumbnail.path || '',
        thumbnailTitle: thumbnail.title,
        thumbnailAlt: thumbnail.alt,
        status: blogByIdData.blogData.status,
        content,
        displayOrder: blogByIdData.blogData.displayOrder,
        description,
        publishedAt: dayjs(blogByIdData.blogData.publishedAt).toISOString(),
        categoryIds: blogByIdData.categories?.map((item) => item.id),
        relatedIds: blogByIdData.relatedBlog?.map((item) => item.id),
        tags: blogByIdData.translations[currentLang].tags,
        blogData: {
          bannerId,
          items,
          storyHook,
          summary,
          conclusion,
        },
      };
      method.reset(objDefault);
    } else {
      method.reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogByIdData, currentLang]);

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (err) {
      setCopyStatus('Failed');
    }
  };

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={idParams ? t('blogDetail.editBlog') : t('blogDetail.createBlog')}
        rightHeader={(
          <Space size={16}>
            <Button
              disabled={(idParams && !roleUpdate) || (!idParams && !roleCreate)}
              onClick={handlePreview}
            >
              <SelectOutlined />
              {t('system.preview')}
            </Button>
            <StatusButtons
              loading={updateStatusLoading}
              status={status}
              isApprove={roleOther.includes(roles.BLOG_APPROVED)}
              handleChangeStatus={(stat) => {
                handleChangeStatusAndSave(stat);
              }}
            />
            {status !== 13 && (
              <Button
                type="primary"
                disabled={
                  (idParams && !roleUpdate) || (!idParams && !roleCreate)
                }
                loading={updateLoading || createLoading}
                onClick={method.handleSubmit(submitForm)}
              >
                <SaveOutlined />
                {t('system.save')}
              </Button>
            )}
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <Spin
          size="large"
          spinning={
            updateLoading || isLoading || createLoading || loadingTemplate
          }
        >
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col xxl={18} xl={16} lg={16}>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <Card type="inner">
                    {/* Tiêu đề  */}
                    <Row gutter={16}>
                      <Col span={12}>
                        <div className="p-editPageTemplate_input">
                          <Typography.Text strong>
                            {t('system.title')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="title"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={(e) => {
                                  onChange(e);
                                  if (
                                    !idParams
                                    || !blogByIdData?.translations[currentLang]
                                      ?.slug
                                  ) {
                                    method.setValue(
                                      'slug',
                                      generateSlug(e.currentTarget.value)
                                    );
                                  }
                                }}
                                error={error?.message}
                                size="large"
                              />
                            )}
                          />
                        </div>
                      </Col>
                      <Col span={12}>
                        <div className="p-editPageTemplate_input">
                          <Typography.Text strong>
                            {t('system.slug')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="slug"
                            control={method.control}
                            render={({
                              field: { value, onChange },
                              fieldState: { error },
                            }) => (
                              <Input
                                className="u-mt-8"
                                value={value}
                                onChange={onChange}
                                error={error?.message}
                                size="large"
                              />
                            )}
                          />
                        </div>
                      </Col>
                    </Row>
                    <div className="u-mt-8">
                      <div className="p-editPageTemplate_input">
                        <Typography.Text strong>Banners</Typography.Text>
                        <Typography.Text strong type="danger">
                          *
                        </Typography.Text>
                        <Controller
                          name="blogData.bannerId"
                          control={method.control}
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <div className="u-mt-8">
                              <Select
                                style={{ width: '100%' }}
                                showSearch
                                value={value}
                                onChange={onChange}
                                onPopupScroll={(e) => {
                                  const {
                                    scrollTop,
                                    scrollHeight,
                                    clientHeight,
                                  } = e.target as any;
                                  if (
                                    scrollHeight - scrollTop - clientHeight
                                    < 100
                                    && hasNextPage
                                  ) {
                                    fetchNextPage();
                                  }
                                }}
                                loading={isLoading}
                                filterOption={false}
                                options={banners?.pages.map((item) => ({
                                  value: item.bannerData.id,
                                  label:
                                    item.translations[currentLang]?.name || '',
                                }))}
                                notFoundContent={
                                  isLoading ? 'Đang tải...' : 'Không có dữ liệu'
                                }
                              />
                              {error?.message && (
                                <span className="a-input_errorMessage">
                                  {error.message}
                                </span>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </div>
                  </Card>
                  <Card type="inner">
                    <div className="site-card-border-less-wrapper">
                      <Space
                        direction="vertical"
                        size={12}
                        style={{ width: '100%' }}
                      >
                        {/* Mô tả  */}
                        <div className="p-editPageTemplate_input">
                          <Typography.Text strong>
                            {t('system.description')}
                            {' '}
                          </Typography.Text>
                          <Typography.Text strong type="danger">
                            *
                          </Typography.Text>
                          <Controller
                            name="description"
                            control={method.control}
                            render={({ field, fieldState }) => (
                              <>
                                <TextArea
                                  {...field}
                                  className="u-mt-8"
                                  value={field.value}
                                  onChange={field.onChange}
                                  size="large"
                                  rows={6}
                                  style={{ minHeight: 50 }}
                                />
                                {fieldState.error && (
                                  <span className="a-input_errorMessage">
                                    {fieldState.error.message}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        </div>
                        {/* Nội dung  */}
                        <div className="p-editPageTemplate_input">
                          <Row style={{ marginBottom: 8 }} justify="space-between" align="middle">
                            <div>
                              <Typography.Text strong>
                                {t('system.content')}
                                {' '}
                              </Typography.Text>
                              <Typography.Text strong type="danger">
                                *
                              </Typography.Text>
                            </div>
                            <Button onClick={() => setModalVisible(true)} type="link">Xem Mẫu</Button>
                          </Row>
                          <Controller
                            name="content"
                            defaultValue=""
                            render={({
                              field: { value, onChange },
                              fieldState,
                            }) => (
                              <>
                                <Editor
                                  value={value}
                                  handleChange={(_event: any, editor: any) => {
                                    const data = editor.getData();
                                    onChange(data);
                                  }}
                                />
                                {fieldState.error && (
                                  <span className="a-input_errorMessage">
                                    {fieldState.error.message}
                                  </span>
                                )}
                              </>
                            )}
                          />
                        </div>
                      </Space>
                    </div>
                  </Card>
                  <Card>
                    {/* Block Story hook */}
                    <div>
                      <Row justify="space-between" gutter={16}>
                        <Typography.Title level={4}>
                          {t('news.storyHook')}
                        </Typography.Title>
                        <div>
                          <Button
                            type="primary"
                            onClick={() => handleHidingSection('blogData.storyHook')}
                          >
                            {hidingSection['blogData.storyHook']
                              ? t('news.remove')
                              : t('system.addNew')}
                          </Button>
                        </div>
                      </Row>
                      {hidingSection['blogData.storyHook'] && (
                        <Row gutter={16}>
                          <Col span={12}>
                            <Col span={24}>
                              <div className="u-mt-16">
                                <Typography.Text strong>
                                  {t('news.title')}
                                </Typography.Text>
                                <Controller
                                  name="blogData.storyHook.title"
                                  render={() => <InputHidden />}
                                />
                                <Controller
                                  control={method.control}
                                  name="blogData.storyHook.title"
                                  defaultValue=""
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <Input
                                      className="u-mt-8"
                                      value={value}
                                      onChange={onChange}
                                      error={error?.message}
                                      size="large"
                                    />
                                  )}
                                />
                              </div>
                            </Col>
                            <Col span={24}>
                              <div className="u-mt-16">
                                <Typography.Text strong>
                                  {t('news.description')}
                                </Typography.Text>
                                <Controller
                                  name="blogData.storyHook.description"
                                  render={() => <InputHidden />}
                                />
                                <Controller
                                  control={method.control}
                                  name="blogData.storyHook.description"
                                  defaultValue=""
                                  render={({
                                    field: { value, onChange },
                                    fieldState,
                                  }) => (
                                    <>
                                      <Editor
                                        value={value || ''}
                                        handleChange={(
                                          _event: any,
                                          editor: any
                                        ) => {
                                          const data = editor.getData();
                                          onChange(data);
                                        }}
                                      />
                                      {/* <TextArea
                                            className="u-mt-8"
                                            value={value}
                                            onChange={onChange}
                                            size="large"
                                            rows={6}
                                            style={{ minHeight: 50 }}
                                          /> */}
                                      {fieldState.error && (
                                        <span className="a-input_errorMessage">
                                          {fieldState.error.message}
                                        </span>
                                      )}
                                    </>
                                  )}
                                />
                              </div>
                            </Col>
                          </Col>
                          <Col span={12}>
                            <div className="u-mt-16">
                              <Controller
                                name="blogData.storyHook.thumbnail.path"
                                render={() => <InputHidden />}
                              />
                              <Controller
                                control={method.control}
                                name="blogData.storyHook.thumbnail.path"
                                defaultValue=""
                                render={({
                                  field: { value, onChange },
                                  fieldState,
                                }) => (
                                  <>
                                    <SelectFile
                                      title={(
                                        <div>
                                          {t('newsDetail.thumbnail')}
                                          {' '}
                                          <Tooltip
                                            title={t('news.thumbnailTooltip')}
                                          >
                                            <QuestionCircleOutlined />
                                          </Tooltip>
                                        </div>
                                      )}
                                      value={value}
                                      name="blogData.storyHook.thumbnail.path"
                                      // titleName="blogData.storyHook.imgTitle"
                                      altName="blogData.storyHook.imgAlt"
                                      handleSelect={(url, title, alt) => {
                                        onChange(url);
                                        // method.setValue('blogData.storyHook.imgTitle'
                                        // , title);
                                        method.setValue(
                                          'blogData.storyHook.thumbnail.alt',
                                          alt
                                        );
                                      }}
                                      handleDelete={() => onChange(undefined)}
                                    />

                                    {fieldState.error && (
                                      <span className="a-input_errorMessage">
                                        {fieldState.error.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          </Col>
                        </Row>
                      )}
                    </div>
                    <Divider />
                    {/* The problem */}
                    <div className="u-mt-24">
                      <Row justify="space-between">
                        <Typography.Title level={4}>
                          {t('news.problem_solution')}
                        </Typography.Title>
                        <div>
                          <Button
                            type="primary"
                            onClick={() => handleHidingSection('blogData.items')}
                          >
                            {hidingSection['blogData.items']
                              ? t('news.remove')
                              : t('system.addNew')}
                          </Button>
                        </div>
                      </Row>
                      {hidingSection['blogData.items'] && (
                        <>
                          {blockFields.map((field, index) => (
                            <Row
                              key={field.id}
                              gutter={16}
                              className="u-mt-16 p-editPageTemplate_metaData_items"
                            >
                              <Col span={12}>
                                <Typography.Text strong>
                                  {t('news.title')}
                                </Typography.Text>
                                <Controller
                                  control={method.control}
                                  name={`blogData.items.${index}.title`}
                                  render={({
                                    field: fieldInput,
                                    fieldState: { error },
                                  }) => (
                                    <Input
                                      {...fieldInput}
                                      size="large"
                                      error={error?.message}
                                    />
                                  )}
                                />
                                <div className="u-mt-16">
                                  <Typography.Text strong className="u-mt-16">
                                    {t('news.description')}
                                  </Typography.Text>
                                  <Controller
                                    control={method.control}
                                    name={`blogData.items.${index}.description`}
                                    render={({
                                      field: fieldInput,
                                      fieldState,
                                    }) => (
                                      <>
                                        {/* <TextArea {...fieldInput}
                                            size="large" rows={6} /> */}
                                        <Editor
                                          value={fieldInput.value || ''}
                                          handleChange={(
                                            _event: any,
                                            editor: any
                                          ) => {
                                            const data = editor.getData();
                                            fieldInput.onChange(data);
                                          }}
                                        />
                                        {fieldState.error && (
                                          <span className="a-input_errorMessage">
                                            {fieldState.error.message}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  />
                                </div>
                              </Col>

                              <Col span={12}>
                                <Controller
                                  control={method.control}
                                  name={`blogData.items.${index}.thumbnail.path`}
                                  render={({
                                    field: { value, onChange },
                                    fieldState,
                                  }) => (
                                    <>
                                      <SelectFile
                                        title={(
                                          <div>
                                            {t('newsDetail.thumbnail')}
                                            {' '}
                                            <Tooltip
                                              title={t('news.thumbnailTooltip')}
                                            >
                                              <QuestionCircleOutlined />
                                            </Tooltip>
                                          </div>
                                        )}
                                        value={value}
                                        // hasAlt
                                        name={`blogData.items.${index}.thumbnail.path`}
                                        titleName={`blogData.items.${index}.thumbnail.title`}
                                        handleSelect={(url, title) => {
                                          onChange(url);
                                          method.setValue(
                                            `blogData.items.${index}.thumbnail.title`,
                                            title
                                          );
                                        }}
                                        handleDelete={() => onChange(undefined)}
                                      />

                                      {fieldState.error && (
                                        <span className="a-input_errorMessage">
                                          {fieldState.error.message}
                                        </span>
                                      )}
                                    </>
                                  )}
                                />
                              </Col>

                              <Col span={24} className="u-mt-8">
                                <Button
                                  danger
                                  disabled={blockFields.length === 1}
                                  onClick={() => removeBlock(index)}
                                >
                                  {t('news.remove')}
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => appendBlock({
                              title: '',
                              description: '',
                              thumbnail: { path: '', title: '', alt: '' },
                            })}
                            disabled={blockFields.length >= 2}
                            className="u-mt-16"
                          >
                            +
                            {' '}
                            {t('news.addRow')}
                          </Button>
                        </>
                      )}
                    </div>
                    <Divider />
                    {/* Compare */}
                    <div className="u-mt-24">
                      <Row justify="space-between">
                        <Typography.Title level={4}>
                          {t('news.keyFeatures')}
                        </Typography.Title>
                        <div>
                          <Button
                            type="primary"
                            onClick={() => handleHidingSection('blogData.summary')}
                          >
                            {hidingSection['blogData.summary']
                              ? t('news.remove')
                              : t('system.addNew')}
                          </Button>
                        </div>
                      </Row>
                      {hidingSection['blogData.summary'] && (
                        <>
                          <div className="u-mt-16">
                            <Typography.Text strong>
                              {t('news.title')}
                            </Typography.Text>
                            <Controller
                              control={method.control}
                              name="blogData.summary.title"
                              render={({
                                field: fieldInput,
                                fieldState: { error },
                              }) => (
                                <Input
                                  {...fieldInput}
                                  error={error?.message}
                                  size="large"
                                />
                              )}
                            />
                          </div>
                          <div>
                            {agentFields.map((field, index) => (
                              <Row
                                key={field.id}
                                gutter={16}
                                className="u-mt-16 p-editPageTemplate_metaData_items"
                              >
                                <Col span={12}>
                                  <Typography.Text strong>
                                    {t('news.title')}
                                  </Typography.Text>
                                  <Controller
                                    control={method.control}
                                    name={`blogData.summary.rows.${index}.title`}
                                    render={({
                                      field: fieldInput,
                                      fieldState: { error },
                                    }) => (
                                      <Input
                                        {...fieldInput}
                                        error={error?.message}
                                        size="large"
                                      />
                                    )}
                                  />
                                  <div className="u-mt-16">
                                    <Typography.Text strong className="u-mt-16">
                                      {t('news.description')}
                                    </Typography.Text>
                                    <Controller
                                      control={method.control}
                                      name={`blogData.summary.rows.${index}.description`}
                                      render={({
                                        field: fieldInput,
                                        fieldState: { error },
                                      }) => (
                                        <>
                                          {/* <TextArea
                                                {...fieldInput}
                                                size="large"
                                                rows={6}
                                                placeholder="Separate lines for each description"
                                              /> */}
                                          <Editor
                                            value={fieldInput.value || ''}
                                            handleChange={(
                                              _event: any,
                                              editor: any
                                            ) => {
                                              const data = editor.getData();
                                              fieldInput.onChange(data);
                                            }}
                                          />
                                          {error && (
                                            <span className="a-input_errorMessage">
                                              {error.message}
                                            </span>
                                          )}
                                        </>
                                      )}
                                    />
                                  </div>
                                </Col>

                                <Col span={12}>
                                  <Controller
                                    control={method.control}
                                    name={`blogData.summary.rows.${index}.thumbnail.path`}
                                    render={({
                                      field: { value, onChange },
                                      fieldState,
                                    }) => (
                                      <>
                                        <SelectFile
                                          title={(
                                            <div>
                                              {t('newsDetail.thumbnail')}
                                              {' '}
                                              <Tooltip
                                                title={t(
                                                  'news.thumbnailTooltip'
                                                )}
                                              >
                                                <QuestionCircleOutlined />
                                              </Tooltip>
                                            </div>
                                          )}
                                          value={value}
                                          name={`blogData.summary.rows.${index}.thumbnail.path`}
                                          titleName={`blogData.summary.rows.${index}.thumbnail.title`}
                                          altName={`blogData.summary.rows.${index}.thumbnail.alt`}
                                          handleSelect={(url, title, alt) => {
                                            onChange(url);
                                            method.setValue(
                                              `blogData.summary.rows.${index}.thumbnail.title`,
                                              title
                                            );
                                            method.setValue(
                                              `blogData.summary.rows.${index}.thumbnail.alt`,
                                              alt
                                            );
                                          }}
                                          handleDelete={() => onChange(undefined)}
                                        />
                                        {fieldState.error && (
                                          <span className="a-input_errorMessage">
                                            {fieldState.error.message}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  />
                                </Col>

                                <Col span={24} className="u-mt-8">
                                  <Button
                                    danger
                                    disabled={agentFields.length === 1}
                                    onClick={() => removeAgent(index)}
                                  >
                                    {t('news.remove')}
                                  </Button>
                                </Col>
                              </Row>
                            ))}
                          </div>

                          <Button
                            type="dashed"
                            onClick={() => appendAgent({
                              title: '',
                              description: '',
                              thumbnail: { path: '', title: '', alt: '' },
                            })}
                            className="u-mt-16"
                            disabled={agentFields.length >= 6}
                          >
                            +
                            {' '}
                            {t('news.addRow')}
                          </Button>
                        </>
                      )}
                    </div>
                    <Divider />
                    {/* Conclusion */}
                    <div className="u-mt-24">
                      <Row justify="space-between">
                        <Typography.Title level={4}>
                          {t('news.conclusion')}
                        </Typography.Title>
                        <div>
                          <Button
                            type="primary"
                            onClick={() => handleHidingSection('blogData.conclusion')}
                          >
                            {hidingSection['blogData.conclusion']
                              ? t('news.remove')
                              : t('system.addNew')}
                          </Button>
                        </div>
                      </Row>
                      {hidingSection['blogData.conclusion'] && (
                        <Row className="u-mt-16" gutter={16}>
                          <Col span={12}>
                            <Typography.Text strong>
                              {t('news.title')}
                            </Typography.Text>
                            <Controller
                              control={method.control}
                              name="blogData.conclusion.title"
                              render={({ field, fieldState: { error } }) => (
                                <Input
                                  {...field}
                                  error={error?.message}
                                  size="large"
                                />
                              )}
                            />

                            <div className="u-mt-16 u-mb-16">
                              <Typography.Text strong className="u-mt-16">
                                {t('news.description')}
                              </Typography.Text>
                              <Controller
                                control={method.control}
                                name="blogData.conclusion.description"
                                render={({ field, fieldState }) => (
                                  <>
                                    {/* <TextArea {...field} size="large" rows={6} /> */}
                                    <Editor
                                      value={field.value || ''}
                                      handleChange={(
                                        _event: any,
                                        editor: any
                                      ) => {
                                        const data = editor.getData();
                                        field.onChange(data);
                                      }}
                                    />
                                    {' '}
                                    {fieldState.error && (
                                      <span className="a-input_errorMessage">
                                        {fieldState.error.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          </Col>
                          <Col span={12}>
                            <Controller
                              control={method.control}
                              name="blogData.conclusion.thumbnail.path"
                              render={({
                                field: { value, onChange },
                                fieldState,
                              }) => (
                                <>
                                  <SelectFile
                                    title={(
                                      <div>
                                        {t('newsDetail.thumbnail')}
                                        {' '}
                                        <Tooltip
                                          title={t('news.thumbnailTooltip2')}
                                        >
                                          <QuestionCircleOutlined />
                                        </Tooltip>
                                      </div>
                                    )}
                                    value={value}
                                    name="blogData.conclusion.thumbnail.title"
                                    titleName="blogData.conclusion.imgTitle"
                                    altName="blogData.conclusion.imgAlt"
                                    handleSelect={(url, title, alt) => {
                                      onChange(url);
                                      method.setValue(
                                        'blogData.conclusion.thumbnail.title',
                                        title
                                      );
                                      method.setValue(
                                        'blogData.conclusion.thumbnail.alt',
                                        alt
                                      );
                                    }}
                                    handleDelete={() => onChange(undefined)}
                                  />
                                  {fieldState.error && (
                                    <span className="a-input_errorMessage">
                                      {fieldState.error.message}
                                    </span>
                                  )}
                                </>
                              )}
                            />
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Card>
                </Space>
                <SeoSection
                  canCreateComment={false}
                  defaultValues={defaultValueSEO}
                  ref={seoSectionRef}
                  socialList={socialList}
                />
              </Col>
              <Col xxl={6} xl={8} lg={8}>
                <div className="u-mb-16">
                  {status && <StatusLabel status={status} bigger />}
                </div>
                <Card className="u-mb-16">
                  <Space
                    direction="vertical"
                    size={16}
                    style={{ width: '100%' }}
                  >
                    {/* Tin tức liên quan  */}
                    <div className="p-editPageTemplate_input">
                      <Typography.Text strong>
                        {t('blogDetail.relatedBlog')}
                        {' '}
                      </Typography.Text>
                      <Controller
                        name="relatedIds"
                        render={(controller) => (
                          <DropdownMultiple
                            controller={controller}
                            type="blog"
                            placeholder={`${t('system.select')} ${t(
                              'blogDetail.relatedBlog'
                            )}`}
                            filterParams={idParams.toString()}
                            locale={currentLang}
                            isHideValueInSelect
                          />
                        )}
                      />
                    </div>
                    {/* Danh mục liên quan */}
                    <div className="p-editPageTemplate_input">
                      <Typography.Text strong>
                        {t('blogDetail.relatedCategory')}
                        {' '}
                      </Typography.Text>
                      <Controller
                        name="categoryIds"
                        control={method.control}
                        render={({ field }) => (
                          <DropdownElement
                            type="blogCategory"
                            placeholder={`${t('system.select')} ${t(
                              'blogDetail.relatedCategory'
                            )}`}
                            locale={currentLang}
                            value={field.value}
                            onChange={field.onChange}
                            multiple={{}}
                          />
                        )}
                      />
                    </div>
                    {/* Tag */}
                    <div className="p-editPageTemplate_input">
                      <Typography.Text strong>
                        {t('blogDetail.tags')}
                        {' '}
                      </Typography.Text>
                      <Controller
                        name="tags"
                        control={method.control}
                        render={({ field }) => (
                          <DropdownElement
                            type="tag"
                            placeholder={`${t('system.select')} ${t(
                              'blogDetail.tags'
                            )}`}
                            locale={currentLang}
                            value={field.value}
                            onChange={field.onChange}
                            isValueSlug
                            multiple={{}}
                          />
                        )}
                      />
                    </div>
                    {/* Thời gian đăng  */}
                    <div>
                      <Typography.Text strong>
                        {t('blogDetail.publishedTime')}
                      </Typography.Text>
                      <Controller
                        name="publishedAt"
                        control={method.control}
                        render={({ field }) => (
                          <DatePicker
                            size="large"
                            value={dayjs(field.value)}
                            onChange={field.onChange}
                            format="DD/MM/YYYY HH:mm"
                            defaultValue={dayjs()}
                            showTime
                            style={{ width: '100%' }}
                          />
                        )}
                      />
                    </div>
                    <div>
                      <Typography.Text strong>
                        {t('blogDetail.displayOrder')}
                      </Typography.Text>
                      <Controller
                        name="displayOrder"
                        control={method.control}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <Input
                            className="u-mt-8"
                            type="number"
                            value={value}
                            onChange={onChange}
                            error={error?.message}
                            size="large"
                          />
                        )}
                      />
                    </div>
                    {/* THUMBNAIL  */}
                    <div className="p-editPageTemplate_input">
                      <Controller
                        name="thumbnail"
                        render={({
                          field: { value, onChange },
                          fieldState,
                        }) => (
                          <>
                            <SelectFile
                              value={value}
                              name="thumbnail"
                              titleName="thumbnailTitle"
                              altName="thumbnailAlt"
                              hasOptions
                              handleSelect={(url, title, alt) => {
                                onChange(url);
                                method.setValue('thumbnailTitle', title);
                                method.setValue('thumbnailAlt', alt);
                              }}
                              handleDelete={() => onChange(undefined)}
                              title={(
                                <>
                                  <Typography.Text strong>
                                    {t('blogDetail.thumbnail')}
                                    {' '}
                                  </Typography.Text>
                                  <Typography.Text strong type="danger">
                                    *
                                    {' '}
                                  </Typography.Text>
                                  <Tooltip title={t('news.thumbnailTooltip')}>
                                    <QuestionCircleOutlined />
                                  </Tooltip>
                                </>
                              )}
                            />
                            {fieldState.error && (
                              <span className="a-input_errorMessage">
                                {fieldState.error.message}
                              </span>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </Space>
                </Card>
                <ManagementInfo
                  classNameCustom="u-mt-16"
                  createdDate={
                    blogByIdData
                      ? dayjs(blogByIdData.blogData.createdAt).fromNow()
                      : ''
                  }
                  createdBy={blogByIdData?.creator.name || ''}
                  lastUpdated={
                    blogByIdData
                      ? dayjs(blogByIdData.blogData.updatedAt).fromNow()
                      : ''
                  }
                  lastUpdatedBy={blogByIdData?.updater.name || ''}
                  languageList={languageOptions}
                  currentLang={currentLang}
                  handleChangeLang={(value) => value && handleChangeLang(value as LanguageCodeTypes)}
                />
                <SeoPlugin
                  commentList={[]}
                  handleOpenPreview={() => seoSectionRef.current?.handleOpenBrowserPreview()}
                  handleOpenSocialPreview={() => seoSectionRef.current?.handleOpenSocialPreview()}
                />
              </Col>
            </Row>
          </FormProvider>
        </Spin>
        <ModalConfirm
          isShow={!!confirm}
          handleCancel={() => setConfirm(undefined)}
          handleConfirm={() => changeLanguageAction(confirm)}
          handleClose={() => setConfirm(undefined)}
        >
          {t('message.confirmSkipInfo')}
        </ModalConfirm>
        <Modal
          width={800}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          title={<Typography.Text className="f-20x24">Xem mẫu các nút</Typography.Text>}
          footer={null}
        >
          {Object.values(BUTTON_EXAMPLES).map((example) => (
            <div key={example.label}>
              <div className="p-editNews_code u-mt-16">
                <Button
                  className="p-editNews_code_copy"
                  onClick={() => handleCopyCode(example.code)}
                >
                  {copyStatus || 'Copy code'}
                </Button>

                <pre className="p-editNews_code_content">
                  <code>{example.code}</code>
                </pre>
              </div>
              <div className="u-mt-16" />
              <div dangerouslySetInnerHTML={{ __html: example.code.trim() }} />
            </div>
          ))}
        </Modal>
      </div>
    </>
  );
};

export default BlogEdit;
